import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ophelos/ui/components/accordion';
import { Alert, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Calendar } from '@ophelos/ui/components/calendar';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { BadgePercent, ChevronRight, CircleAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { AffordabilityCommitment } from '@/components/payments/AffordabilityCommitment';
import { OfferOutcome } from '@/components/payments/OfferOutcome';
import { PayerEmailField } from '@/components/payments/PayerEmailField';
import { PaymentCountStepper } from '@/components/payments/PaymentCountStepper';
import { PaymentMethodSelectionSimple } from '@/components/payments/PaymentMethodSelectionSimple';
import { PlanScheduleList } from '@/components/payments/PlanScheduleList';
import { PlanSummaryHero } from '@/components/payments/PlanSummaryHero';
import { SegmentedControl } from '@/components/payments/SegmentedControl';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { applyCredit, creditApplied } from '@/lib/accountNet';
import { useOfferGate } from '@/hooks/useOfferGate';
import { usePayerEmail } from '@/hooks/usePayerEmail';
import { usePaymentMethodAvailability } from '@/hooks/usePaymentMethodAvailability';
import { usePlanMethodAvailability } from '@/hooks/usePlanMethodAvailability';
import { useTranslation } from '@/hooks/useTranslation';
import { discountedAmountMillicents } from '@/lib/discount-offers';
import { PaymentApiError, usePaymentApi } from '@/lib/payment-api';
import { fromSubunits } from '@/lib/currency';
import { baseInstalmentSubunits, buildPlanSchedule, oneMonthAhead, toApiSchedule, toScheduleDate } from '@/lib/payment-schedule';
import { ACCOUNT_SCOPE, paymentReturnUrl } from '@/lib/paymentReturnUrl';
import { parsePlanCount, parsePlanStartDate } from '@/lib/redirect';
import { selectPaymentTier } from '@/lib/paymentPlans';
import { type PaymentPlanData, samePlanTerms } from '@/pages/_partials/_singlePaymentPartials/PaymentPlanTab';
import type { DebtData, PaymentPlanConfiguration } from '@/types';

/**
 * Account-scoped plans support automatic plan methods only (card/SEPA/Bacs).
 * Multibanco / MB Way at account scope is deferred to #3873. The page
 * gates the tab entirely when no automatic methods are configured, so by
 * the time this component renders at least one is available.
 */
const ACCOUNT_PLAN_AUTOMATIC_METHODS: ReadonlyArray<PaymentMethod> = ['card', 'sepa', 'bacs'];

interface AccountPaymentPlanTabProps {
  accountId: string | null;
  debts: DebtData[];
  totalAmount: number;
  paymentPlanConfig?: PaymentPlanConfiguration;
  /** Card plans advance to the review step instead of creating the session here. */
  onReview?: (planData: PaymentPlanData) => void;
  /**
   * Message from the parent explaining why the customer was returned here — set when a
   * reviewed plan was refused because its pricing lapsed. Shown in the tab's own alert.
   */
  returnNotice?: string | null;
  /** Deep-link prefill (advisory; clamped to config). Set when the budget assessment hands the
   *  customer back to the plan they were configuring. */
  initialFrequency?: string | null;
  initialStartDate?: string | null;
  initialPaymentCount?: string | null;
  /** The method chosen on the plan being edited, so "Edit plan" restores it (AC6). */
  initialMethod?: PaymentMethod | null;
  /**
   * The plan a verdict has already been taken on, held by the parent across "Edit plan". Terms
   * matching it are not resubmitted — see `handleContinue`.
   */
  decidedPlan?: PaymentPlanData | null;
  /**
   * Called immediately before an offer is submitted, so the parent can drop any verdict it was
   * holding: this submission may supersede it, and a superseded verdict must not be reused.
   */
  onOfferSubmitted?: () => void;
  /** True on return from a budget assessment. */
  assessmentReturned?: boolean;
}

export function AccountPaymentPlanTab({
  accountId,
  debts,
  totalAmount: propTotalAmount,
  paymentPlanConfig,
  onReview,
  returnNotice = null,
  initialFrequency,
  initialStartDate,
  initialPaymentCount,
  initialMethod,
  decidedPlan = null,
  onOfferSubmitted,
  assessmentReturned = false,
}: AccountPaymentPlanTabProps) {
  const { t } = useTranslation();
  const { createPaymentSession } = usePaymentApi();
  const { activeOffers } = useDebtData();
  const { config } = useConfiguration();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const { needsEmail, payerEmail, setPayerEmail, emailValid, persistPayerEmail } = usePayerEmail();
  // `case_id: null` — an account-wide offer covers every payable case under the account.
  const offerGate = useOfferGate({ caseId: null, basePath: '/account-payment' });
  const currency = debts[0]?.currency ?? 'EUR';

  // Filter configured plan methods to automatic-only (card/SEPA/Bacs) — account-scoped
  // plan creation supports those only. The parent page gates the tab off when none is
  // both configured and backed by an enabled provider; the guard below covers a deep link.
  const { data: methodConfigAvailability } = usePaymentMethodAvailability();
  const configuredPlanMethods = config.payment?.planPaymentMethods as PaymentMethod[] | undefined;
  const availablePlanMethods = useMemo<PaymentMethod[]>(
    () => (configuredPlanMethods ?? []).filter((m): m is PaymentMethod => ACCOUNT_PLAN_AUTOMATIC_METHODS.includes(m) && methodConfigAvailability[m]),
    [configuredPlanMethods, methodConfigAvailability],
  );

  // Combined payment_plan discount across cases. Each eligible case contributes
  // its own discount; cases without an offer contribute their full balance.
  const { discountedTotal, payableTotal, earliestExpiry, hasActiveDiscount, perDebt, pricedOfferIds } = useMemo(() => {
    const rows: Array<{ id: string; creditor: string; original: number; discounted: number; hasOffer: boolean }> = [];
    let total = 0;
    // Each debt's credit comes off that debt's own price, and only then are they summed:
    // pooling the credits first would let one debt's surplus pay down another, and the
    // plan would stop matching the per-debt figures the customer was shown.
    let payable = 0;
    let credits = 0;
    let hasDiscount = false;
    let earliest: number | null = null;
    const pricedIds: string[] = [];
    for (const debt of debts) {
      const off = activeOffers[debt.id];
      const debtCurrentMillicents = Math.round(debt.currentBalance * 100_000);
      const dMc = off ? discountedAmountMillicents(off, 'payment_plan', debtCurrentMillicents) : null;
      const active = off && dMc != null && off.status !== 'expired' && off.status !== 'superseded';
      const discounted = active ? (dMc as number) / 100_000 : debt.currentBalance;
      rows.push({
        id: debt.id,
        creditor: debt.originalCreditor,
        original: debt.currentBalance,
        discounted,
        hasOffer: Boolean(active),
      });
      total += discounted;
      payable += applyCredit(discounted, debt.creditApplied ?? 0);
      credits += debt.creditApplied ?? 0;
      if (active) {
        hasDiscount = true;
        pricedIds.push(off.offerId);
        if (off.expiresAt) {
          const t = new Date(off.expiresAt).getTime();
          if (earliest === null || t < earliest) earliest = t;
        }
      }
    }
    return {
      discountedTotal: hasDiscount ? total : propTotalAmount,
      // Only the discounted path needs the per-debt walk. Without a discount each debt's
      // credit is capped at its own balance already, so the aggregate cannot overflow onto
      // another debt, and the prop stays the caller's figure to own.
      payableTotal: hasDiscount ? payable : applyCredit(propTotalAmount, credits),
      earliestExpiry: earliest != null ? new Date(earliest).toISOString() : null,
      hasActiveDiscount: hasDiscount,
      perDebt: rows,
      pricedOfferIds: pricedIds,
    };
  }, [debts, activeOffers, propTotalAmount]);
  const showDiscount = hasActiveDiscount;
  // Credit applies after the discount, and here rather than to the `totalAmount` prop
  // because the discount path above rebuilds the total from the per-case balances and
  // drops it. Savings below stay on the pre-credit totals, so the copy still describes
  // the offer rather than the credit.
  const totalAmount = payableTotal;
  // Guarded on the discounted subtotal, not the gross the parent gates on: a discount can
  // drop the balance under the credit, leaving a plan that could only schedule zeroes.
  const nothingLeftToPay = creditApplied(debts) > 0 && totalAmount <= 0;
  const totalPlanSavings = showDiscount ? propTotalAmount - discountedTotal : 0;
  const effectiveDiscountPct = showDiscount && propTotalAmount > 0 ? Math.round((totalPlanSavings / propTotalAmount) * 100) : 0;
  const canShowBreakdown = debts.length > 1;

  // Get available frequencies from config, defaulting to monthly only
  const configuredFrequencies = paymentPlanConfig?.paymentFrequencies ?? ['monthly'];

  // Map config frequencies to UI-friendly format (bi-weekly in config -> biweekly in UI)
  const frequencies = useMemo(() => {
    const allFrequencies = [
      { value: 'weekly' as const, label: t('paymentPlan.weekly'), configKey: 'weekly' },
      { value: 'biweekly' as const, label: t('paymentPlan.biweekly'), configKey: 'bi-weekly' },
      { value: 'monthly' as const, label: t('paymentPlan.monthly'), configKey: 'monthly' },
    ];
    return allFrequencies.filter((f) => configuredFrequencies.includes(f.configKey as 'monthly' | 'bi-weekly' | 'weekly'));
  }, [t, configuredFrequencies]);

  // Select tier based on total debt amount
  const tier = selectPaymentTier(totalAmount, paymentPlanConfig);
  const maxDurationMonths = tier?.maxDurationMonths ?? 12;

  // Get default frequency - prefer longest duration (monthly > biweekly > weekly)
  const defaultFrequency = frequencies.length > 0 ? frequencies[frequencies.length - 1].value : 'monthly';

  const [startDate, setStartDate] = useState<Date | undefined>(() => parsePlanStartDate(initialStartDate) ?? new Date());
  // The segment is its own state: "Later" keeps today selected in the calendar until the
  // customer picks another day, so it cannot be derived from whether the date is today.
  const [firstPayment, setFirstPayment] = useState<'today' | 'later'>(() => {
    const restored = parsePlanStartDate(initialStartDate);
    return restored && toScheduleDate(restored) !== toScheduleDate(new Date()) ? 'later' : 'today';
  });
  const [frequency, setFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>(() =>
    (initialFrequency === 'weekly' || initialFrequency === 'biweekly' || initialFrequency === 'monthly') &&
    frequencies.some((f) => f.value === initialFrequency)
      ? initialFrequency
      : defaultFrequency,
  );
  const [paymentCount, setPaymentCount] = useState(() => parsePlanCount(initialPaymentCount) ?? 6);

  // Calculate max payments based on frequency (maxDurationMonths is always in months)
  const maxPayments = useMemo(() => {
    if (frequency === 'weekly') {
      return maxDurationMonths * 4; // ~4 weeks per month
    }
    if (frequency === 'biweekly') {
      return maxDurationMonths * 2; // ~2 bi-weekly periods per month
    }
    return maxDurationMonths; // monthly
  }, [frequency, maxDurationMonths]);

  // Clamp paymentCount to valid range when maxPayments changes
  useEffect(() => {
    setPaymentCount((prev) => Math.min(Math.max(prev, 2), maxPayments));
  }, [maxPayments]);

  // Unrounded: `onReview` hands this to AccountPayment, which rebuilds the plan total as
  // paymentAmount * paymentCount. Flooring it here would undercharge by the remainder.
  const paymentAmount = totalAmount / paymentCount;
  // What the plan actually charges per instalment — see baseInstalmentSubunits.
  const baseInstalment = fromSubunits(baseInstalmentSubunits(totalAmount, paymentCount));
  const { today, maxDate, todayOnly, maxDateOnly } = useMemo(() => {
    const today = new Date();
    const maxDate = oneMonthAhead(today);
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    return { today, maxDate, todayOnly, maxDateOnly };
  }, []);
  const isDateDisabled = useCallback(
    (date: Date) => {
      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return dateOnly < todayOnly || dateOnly > maxDateOnly;
    },
    [todayOnly, maxDateOnly],
  );

  const { paymentMethod, setPaymentMethod, methodDisabledReasons } = usePlanMethodAvailability(availablePlanMethods, startDate, initialMethod);

  const isToday =
    startDate &&
    startDate.getDate() === today.getDate() &&
    startDate.getMonth() === today.getMonth() &&
    startDate.getFullYear() === today.getFullYear();

  const handleContinue = async () => {
    if (!accountId || !startDate || isProcessing || nothingLeftToPay) return;

    if (!paymentMethod) {
      setPaymentError(Object.values(methodDisabledReasons)[0] ?? t('paymentPlan.setupFailed'));
      return;
    }

    if (!emailValid) {
      setPaymentError(t('paymentPlan.emailRequired'));
      return;
    }

    // Persisted before the offer is decided, not at checkout: a referred or assessment-bound
    // offer never reaches checkout, and Ops is asked to review a plan whose only contact detail
    // the customer just typed. Ahead of `setIsProcessing`, so a failure leaves the CTA usable.
    try {
      await persistPayerEmail();
    } catch {
      setPaymentError(t('paymentPlan.emailRequired'));
      return;
    }

    const configured = {
      startDate,
      frequency,
      paymentCount,
      paymentMethod,
      paymentAmount,
      totalAmount,
      originalTotalAmount: showDiscount ? propTotalAmount : undefined,
      pricedOfferIds: showDiscount ? pricedOfferIds : undefined,
    };

    // Every method is gated here, ahead of any review screen: nobody should read an
    // "Authorise card & pay" screen and only then be told they need to answer budget questions.
    // Mirrors `PaymentPlanTab`.
    //
    // `isProcessing` is held across both calls so the CTA cannot be pressed twice — a second
    // submission would land on `uq_plan_requests_pending_*` as a conflict.
    setIsProcessing(true);
    setPaymentError(null);

    // A verdict already taken on these exact numbers stands, so "Edit plan" and straight back
    // out again records one accepted offer rather than one per press.
    if (!decidedPlan || !samePlanTerms(decidedPlan, configured)) {
      onOfferSubmitted?.();
      const decided = await offerGate.submit({ frequency, count: paymentCount, startDate, totalAmount });
      if (decided.go === 'assessment') {
        navigate(decided.url);
        return;
      }
      if (decided.go === 'stop') {
        setIsProcessing(false);
        return;
      }
    }

    // Card plans stop on the review step, which is now the last thing before the card sheet.
    // Every other method has no review screen and sets its mandate up directly.
    if (paymentMethod === 'card') {
      setIsProcessing(false);
      onReview?.(configured);
      return;
    }

    executeAccountPlan(startDate, frequency, paymentCount, totalAmount);
  };

  /**
   * `planTotal` is passed rather than closed over because an accepted offer can sit on a
   * confirmation screen while `totalAmount` moves — a discount expiring triggers a refresh —
   * and the schedule has to divide the figure the verdict was taken on.
   */
  const executeAccountPlan = async (
    planStartDate: Date,
    planFrequency: 'weekly' | 'biweekly' | 'monthly',
    planPaymentCount: number,
    planTotal: number,
  ) => {
    if (!accountId || !paymentMethod) return;

    setIsProcessing(true);
    setPaymentError(null);

    try {
      const baseUrl = window.location.origin;
      const successUrl = paymentReturnUrl({ flow: 'plan', scope: ACCOUNT_SCOPE });
      const cancelUrl = `${baseUrl}/account-payment`;

      const schedule = toApiSchedule(buildPlanSchedule(planTotal, planPaymentCount, planStartDate, planFrequency));

      const response = await createPaymentSession({
        account_id: accountId,
        currency,
        payment_method: paymentMethod,
        payment_type: 'payment_plan',
        description: `Payment plan covering ${debts.length} debts (${planPaymentCount} ${planFrequency} payments)`,
        success_url: successUrl,
        cancel_url: cancelUrl,
        payment_plan_schedule: schedule,
        payment_plan_frequency: planFrequency,
      });

      if (response.payment_plan_id || response.checkout_url) {
        window.location.href = response.checkout_url ?? successUrl;
        return;
      }

      throw new Error('Invalid payment response: no plan ID or checkout URL returned');
    } catch (err) {
      setIsProcessing(false);
      if (err instanceof PaymentApiError) {
        // Still reachable although the account surface blocks an arranged account: Connect
        // swallows a failed plan fetch, so a plan-covered case can read as plan-free, and the
        // snapshot predates any plan landing mid-session. Same relabel as the sibling tabs —
        // the server's detail tells the reader to cancel a plan they may not have made.
        setPaymentError(err.status === 409 ? t('payment.planConflict') : err.message);
      } else {
        setPaymentError(t('paymentPlan.setupFailed'));
      }
    }
  };

  // After every hook, so a verdict arriving mid-mount cannot change the hook count.
  //
  // Referred verdicts only: an accepted offer goes straight on to review or setup, so it never
  // holds an outcome here.
  if (availablePlanMethods.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center px-4">
        <Alert variant="subtle" className="max-w-md">
          <CircleAlert className="size-4" />
          <AlertTitle>{t('paymentPlan.notAvailable')}</AlertTitle>
          <AlertDescription>{t('paymentPlan.noMethodsConfigured')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (offerGate.outcome) {
    const held = offerGate.outcome;
    return (
      <OfferOutcome
        outcome={held.outcome}
        terms={held.terms}
        currency={currency}
        frequencyLabel={frequencies.find((f) => f.value === held.terms.frequency)?.label ?? held.terms.frequency}
        onChoosePlan={offerGate.reset}
        onDone={() => navigate('/dashboard')}
        isBusy={isProcessing}
        error={paymentError}
      />
    );
  }

  return (
    <div className="w-full flex-1 min-h-0 stack items-center px-2 xs:px-4">
      <div className="flex-1 w-full stack space-y-4">
        <div className="max-w-md mx-auto w-full space-y-4">
          {showDiscount && (
            <Alert variant="warning">
              <BadgePercent />
              <AlertTitle>{t('discount.percentApplied', { pct: effectiveDiscountPct })}</AlertTitle>
              <AlertDescription className="flex">
                <Trans
                  i18nKey="discount.youSaveAcrossPlan"
                  components={{
                    amount: <CurrencyDisplay amount={totalPlanSavings} currency={currency} className="font-semibold inline-flex gap-0" />,
                  }}
                />
              </AlertDescription>
            </Alert>
          )}

          {/* Header — when there's more than one debt, the line acts as the trigger for a per-debt breakdown */}
          {canShowBreakdown ? (
            <Accordion type="single" collapsible>
              <AccordionItem value="breakdown" className="border-b-0">
                <AccordionTrigger className="py-1 flex justify-center gap-2 text-sm text-muted-foreground font-normal hover:bg-transparent">
                  <div className="stack items-center">
                    <span>{t('paymentPlan.createPlanCovering')} </span>
                    <span className="text-foreground font-semibold">
                      {debts.length} {debts.length === 1 ? t('paymentPlan.outstandingDebt') : t('paymentPlan.outstandingDebts')}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="divide-y divide-border p-2">
                  {perDebt.map((row) => (
                    <div key={row.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <span className="truncate text-foreground">{row.creditor}</span>
                      <span className="flex items-center gap-2 text-right shrink-0">
                        {row.hasOffer ? (
                          <>
                            <CurrencyDisplay amount={row.original} currency={currency} className="line-through text-muted-foreground text-xs" />
                            <CurrencyDisplay amount={row.discounted} currency={currency} className="font-semibold" />
                          </>
                        ) : (
                          <CurrencyDisplay amount={row.original} currency={currency} className="font-semibold" />
                        )}
                      </span>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : (
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {t('paymentPlan.createPlanCovering')}{' '}
                <span className="text-foreground font-semibold">
                  {debts.length} {debts.length === 1 ? t('paymentPlan.outstandingDebt') : t('paymentPlan.outstandingDebts')}
                </span>
              </p>
            </div>
          )}
        </div>
        <section className="stack gap-6 w-full max-w-md mx-auto">
          <div className="stack gap-1.5">
            <h3 className="text-sm font-semibold">{t('paymentPlan.numberOfPayments')}</h3>
            <PaymentCountStepper count={paymentCount} onChange={setPaymentCount} min={2} max={maxPayments} disabled={isProcessing} />
          </div>

          <div className="stack gap-1.5">
            <h3 className="text-sm font-semibold">{t('paymentPlan.frequency')}</h3>
            <SegmentedControl
              options={frequencies}
              value={frequency}
              onChange={setFrequency}
              disabled={isProcessing}
              aria-label={t('paymentPlan.frequency')}
            />
          </div>

          <PlanSummaryHero
            instalment={baseInstalment}
            currency={currency}
            paymentCount={paymentCount}
            frequencyLabel={frequencies.find((f) => f.value === frequency)?.label ?? ''}
            total={totalAmount}
            originalInstalment={showDiscount ? propTotalAmount / paymentCount : undefined}
            originalTotal={showDiscount ? propTotalAmount : undefined}
            expiresAt={earliestExpiry}
          />

          <div className="stack gap-1.5 w-full">
            <h3 className="text-sm font-semibold">{t('paymentPlan.firstPayment')}</h3>
            <SegmentedControl
              options={[
                { value: 'today', label: t('paymentPlan.today') },
                { value: 'later', label: t('paymentPlan.later') },
              ]}
              value={firstPayment}
              onChange={(choice) => {
                setFirstPayment(choice);
                if (choice === 'today' || !startDate) setStartDate(new Date());
              }}
              disabled={isProcessing}
              aria-label={t('paymentPlan.firstPayment')}
            />
            {firstPayment === 'later' && (
              <div className="rounded-2xl border bg-card p-2 mt-2 w-full max-w-xs mx-auto">
                <Calendar
                  mode="single"
                  selected={startDate}
                  captionLayout="dropdown-months"
                  startMonth={today}
                  endMonth={maxDate}
                  disabled={isDateDisabled}
                  onSelect={(date) => date && setStartDate(date)}
                  className="w-full bg-transparent"
                  classNames={{ root: 'w-full' }}
                />
              </div>
            )}
          </div>

          {startDate && (
            <PlanScheduleList
              instalments={buildPlanSchedule(totalAmount, paymentCount, startDate, frequency)}
              currency={currency}
              totalAmount={totalAmount}
              startsToday={!!isToday}
            />
          )}
        </section>

        {needsEmail && <PayerEmailField id="account-plan-payer-email" value={payerEmail} onChange={setPayerEmail} />}

        {/* Always shown, single configured method included: the CTA reads "Continue" for every
            method, so this row is the only place the customer is told what they are setting up. */}
        <div className="max-w-md mx-auto w-full space-y-2">
          <p className="text-sm font-medium text-muted-foreground">{t('paymentPlan.selectPaymentMethod')}</p>
          <PaymentMethodSelectionSimple
            selectedMethod={paymentMethod}
            onSelect={setPaymentMethod}
            availableMethods={availablePlanMethods}
            disabledReasons={methodDisabledReasons}
            disabled={isProcessing}
          />
        </div>

        <div className="max-w-md w-full mx-auto stack gap-4">
          {/* Back from the assessment. Says nothing about the outcome: a situation disclosed on
              the assessment refers the offer, and that check runs first. */}
          {assessmentReturned && (
            <Alert variant="subtle">
              <CircleAlert />
              <AlertDescription>{t('paymentOffer.assessmentSaved')}</AlertDescription>
            </Alert>
          )}

          {(paymentError || offerGate.error || returnNotice) && (
            <Alert variant="destructive">
              <CircleAlert />
              <AlertDescription>{paymentError ?? offerGate.error ?? returnNotice}</AlertDescription>
            </Alert>
          )}
        </div>

        <AffordabilityCommitment />
      </div>

      <BottomActionBar>
        <Button onClick={handleContinue} size="xl" className="group w-full max-w-sm" disabled={isProcessing || !emailValid || nothingLeftToPay}>
          {t('common.continue')}
          <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </BottomActionBar>
    </div>
  );
}
